/**
 * "Your journey map" — rendered from the Vision Board's dreams + action steps.
 * Nothing here is invented: every stop is a dream you pinned, every checkpoint
 * an action step you set on /vision-board.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Circle, Flag, Sparkles, Target, Clock, Flame } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  buildJourneyMap,
  fetchVisionBoard,
  readLocalVisionBoard,
  type JourneyMap,
  type JourneyMapStop,
} from "@/lib/journeyMap";

export default function VisionJourneyMap() {
  const [map, setMap] = useState<JourneyMap>(() => buildJourneyMap(readLocalVisionBoard()));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    fetchVisionBoard().then((vb) => {
      if (!active) return;
      setMap(buildJourneyMap(vb));
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, []);

  return (
    <section aria-labelledby="journey-map-heading">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h2 id="journey-map-heading" className="font-display text-2xl font-bold text-foreground">
            Your journey map
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Straight from your Vision Board — the dreams you pinned and the steps you set under each one.
          </p>
        </div>
        <Button asChild size="sm" variant="outline" className="min-h-11 px-4">
          <Link to="/vision-board">Open Vision Board</Link>
        </Button>
      </div>

      {map.stops.length === 0 ? (
        <Card className="border-dashed border-border/70 bg-card/50">
          <CardContent className="p-6 sm:p-8 text-center">
            <div className="mx-auto h-12 w-12 rounded-2xl bg-primary/15 text-primary grid place-items-center">
              <Sparkles className="h-5 w-5" />
            </div>
            <h3 className="font-display text-lg font-semibold text-foreground mt-4">
              {loading ? "Loading your map…" : "Your map starts with a dream"}
            </h3>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              Pin a role or a dream on your Vision Board and add a few action steps. They show up
              here as the stops on your journey.
            </p>
            <div className="mt-5 flex flex-wrap gap-2 justify-center">
              <Button asChild size="sm">
                <Link to="/vision-board">Pin your first dream</Link>
              </Button>
              <Button asChild size="sm" variant="outline">
                <Link to="/careers">Browse careers</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <MapTile icon={Target} label="Steps done" value={`${map.doneSteps}/${map.totalSteps}`} />
            <MapTile icon={Sparkles} label="XP earned" value={`${map.xpEarned}`} sub={`of ${map.xpAvailable}`} />
            <MapTile icon={Clock} label="Hours left" value={`${map.hoursRemaining}`} sub="on open steps" />
            <MapTile icon={Flame} label="Daily streak" value={`${map.streak}`} sub={`${map.dailyDone}/${map.dailyTotal} today`} />
          </div>

          <ol className="relative border-l-2 border-dashed border-border ml-4 space-y-6">
            {map.stops.map((stop, i) => (
              <Stop key={stop.dreamId} stop={stop} index={i} />
            ))}
          </ol>
        </>
      )}
    </section>
  );
}

function MapTile({
  icon: Icon, label, value, sub,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <Card className="border-border/50 bg-card/50">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Icon className="h-3.5 w-3.5" />
          <span className="text-xs font-medium">{label}</span>
        </div>
        <p className="mt-1.5 font-display text-2xl font-bold text-foreground">{value}</p>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </CardContent>
    </Card>
  );
}

const STATUS_LABEL: Record<JourneyMapStop["status"], string> = {
  achieved: "Achieved",
  in_progress: "In progress",
  not_started: "Not started",
};

function Stop({ stop, index }: { stop: JourneyMapStop; index: number }) {
  return (
    <motion.li
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.35, delay: Math.min(index * 0.04, 0.3) }}
      className="relative pl-8"
    >
      <span
        aria-hidden="true"
        className={cn(
          "absolute -left-[13px] top-1 grid h-6 w-6 place-items-center rounded-full ring-4 ring-background",
          stop.status === "achieved"
            ? "bg-primary text-primary-foreground"
            : stop.status === "in_progress"
              ? "bg-accent text-accent-foreground"
              : "bg-muted text-muted-foreground",
        )}
      >
        {stop.status === "achieved" ? <Check className="h-3.5 w-3.5" /> : <Flag className="h-3 w-3" />}
      </span>

      <Card className="border-border/50 bg-card/60">
        <CardContent className="p-5">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="font-display text-lg font-semibold text-foreground">{stop.title}</h3>
              {stop.note && <p className="text-sm text-muted-foreground mt-1">{stop.note}</p>}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {stop.targetYear && <Badge variant="outline">By {stop.targetYear}</Badge>}
              <Badge variant="outline" className="capitalize">{stop.priority} priority</Badge>
              <Badge variant="secondary">{STATUS_LABEL[stop.status]}</Badge>
            </div>
          </div>

          {stop.totalSteps > 0 ? (
            <>
              <div className="mt-4 flex items-center gap-3">
                <div className="h-2 flex-1 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${stop.progressPct}%` }}
                  />
                </div>
                <span className="text-xs text-muted-foreground shrink-0">
                  {stop.doneCount}/{stop.totalSteps} steps
                </span>
              </div>

              <ul className="mt-4 space-y-2">
                {stop.steps.map((s) => (
                  <li key={s.id} className="flex items-start gap-2.5 text-sm">
                    {s.done ? (
                      <Check className="h-4 w-4 mt-0.5 text-primary shrink-0" aria-hidden="true" />
                    ) : (
                      <Circle className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" aria-hidden="true" />
                    )}
                    <span className={cn("min-w-0", s.done ? "text-muted-foreground line-through" : "text-foreground")}>
                      {s.label}
                      <span className="sr-only">{s.done ? " (done)" : " (to do)"}</span>
                      {!s.done && s.hours ? (
                        <span className="text-muted-foreground"> · ~{s.hours}h</span>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>

              {stop.nextStep && (
                <p className="mt-4 text-sm text-foreground">
                  <span className="text-muted-foreground">Next: </span>
                  {stop.nextStep.label}
                </p>
              )}
            </>
          ) : (
            <p className="mt-4 text-sm text-muted-foreground">
              No steps yet. Add an action plan on your Vision Board and this stop fills in.
            </p>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <Button asChild size="sm" variant={stop.totalSteps ? "outline" : "default"}>
              <Link to="/vision-board#actions">
                {stop.totalSteps ? "Update steps" : "Add steps"}
              </Link>
            </Button>
            {stop.careerId && (
              <Button asChild size="sm" variant="ghost">
                <Link to={`/careers/${stop.careerId}`}>See this role</Link>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.li>
  );
}
